import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';
import type { CreateClientInput, UpdateClientInput } from '@shared/validation/client-schemas';

export interface ClientDTO {
  id: string;
  nombre: string;
  ruc_ci: string | null;
  telefono: string | null;
  email: string | null;
  direccion: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientDetailDTO extends ClientDTO {
  projects_count: number;
}

interface ClientsResponse {
  data: ClientDTO[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export function useClients(filters?: { search?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: queryKeys.clients.list(filters),
    queryFn: async () => {
      const response = await api.get<ClientsResponse>('/clients', { params: filters });
      return response.data;
    },
  });
}

export function useClient(id: string) {
  return useQuery({
    queryKey: queryKeys.clients.detail(id),
    queryFn: async () => {
      const response = await api.get<{ data: ClientDetailDTO }>(`/clients/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateClientInput) => {
      const response = await api.post<{ data: ClientDTO }>('/clients', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateClientInput }) => {
      const response = await api.put<{ data: ClientDTO }>(`/clients/${id}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
    },
  });
}

/**
 * Debounced client search for autocomplete.
 * Enabled only when search string is >= 2 chars.
 */
export function useClientSearch(search: string) {
  return useQuery({
    queryKey: queryKeys.clients.list({ search }),
    queryFn: async () => {
      const response = await api.get<ClientsResponse>('/clients', {
        params: { search, limit: 10 },
      });
      return response.data;
    },
    enabled: search.length >= 2,
  });
}

// ── Delete client (hard, only if no projects) ──
export function useDeleteClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/clients/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      toast('success', 'Cliente eliminado');
    },
  });
}
